import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fmtEur, fmtMwhc, fmtDate, SECTOR_META } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { FilePlus2, Eye, Copy, Trash2, Download, Search, Loader2, FolderKanban } from "lucide-react";
import { toast } from "sonner";

export default function Projects() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);
  const [q, setQ] = useState("");
  const [toDelete, setToDelete] = useState(null);

  const load = () => api.get("/projects").then((r) => setProjects(r.data));

  useEffect(() => {
    load();
  }, []);

  const duplicate = async (id) => {
    try {
      await api.post(`/projects/${id}/duplicate`);
      toast.success("Projet dupliqué");
      load();
    } catch {
      toast.error("Échec de la duplication");
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/projects/${toDelete.id}`);
      toast.success("Projet supprimé");
      setToDelete(null);
      load();
    } catch {
      toast.error("Échec de la suppression");
    }
  };

  const exportCsv = (p) => {
    const rows = [["Fiche", "Opération", "Bloc", "Secteur", "kWh cumac", "Prime (€)"]];
    (p.result?.operations || []).forEach((o) => rows.push([o.fiche_code, o.op_name || o.name, o.block_name || "", o.sector, o.kwhc, o.prime]));
    rows.push(["", "", "", "TOTAL", p.result?.total_kwhc, p.result?.total_prime]);
    const csv = rows.map((r) => r.map((c) => `"${c}"`).join(";")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${p.name}.csv`;
    a.click();
  };

  if (!projects) {
    return (
      <div className="grid place-items-center py-32">
        <Loader2 className="h-8 w-8 animate-spin text-genis" />
      </div>
    );
  }

  const filtered = projects.filter(
    (p) => p.name.toLowerCase().includes(q.toLowerCase()) || (p.client || "").toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-genis">Portefeuille</p>
          <h1 className="text-3xl font-extrabold font-heading tracking-tight text-genis-deep dark:text-white">Mes projets</h1>
        </div>
        <Button onClick={() => navigate("/simulation/new")} className="bg-genis hover:bg-genis-dark text-white" data-testid="projects-new-btn">
          <FilePlus2 className="h-4 w-4 mr-1.5" /> Nouvelle simulation
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un projet ou un client…" className="pl-9" data-testid="projects-search-input" />
      </div>

      {filtered.length === 0 ? (
        <Card className="py-16 text-center">
          <div className="mx-auto h-16 w-16 grid place-items-center rounded-2xl bg-genis/10 text-genis mb-4">
            <FolderKanban className="h-8 w-8" />
          </div>
          <p className="text-muted-foreground">Aucun projet trouvé.</p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-secondary/50 text-left">
                  <th className="px-4 py-3 font-semibold">Projet</th>
                  <th className="px-4 py-3 font-semibold hidden md:table-cell">Zone</th>
                  <th className="px-4 py-3 font-semibold hidden lg:table-cell">Opérations</th>
                  <th className="px-4 py-3 font-semibold text-right">MWhc</th>
                  <th className="px-4 py-3 font-semibold text-right">Prime</th>
                  <th className="px-4 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.map((p) => (
                  <tr key={p.id} className="hover:bg-secondary/40 transition-colors" data-testid={`project-row-${p.id}`}>
                    <td className="px-4 py-3">
                      <button onClick={() => navigate(p.status === "brouillon" ? `/simulation/new?id=${p.id}` : `/projects/${p.id}`)} title={p.status === "brouillon" ? "Reprendre le brouillon" : undefined} className="text-left" data-testid={`project-open-${p.id}`}>
                        <div className="font-semibold text-genis-deep dark:text-white hover:text-genis flex items-center gap-2">
                          {p.name}
                          {p.status === "brouillon" && <span className="rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700" data-testid={`project-draft-badge-${p.id}`}>Brouillon</span>}
                        </div>
                        <div className="text-xs text-muted-foreground">{p.client || "—"} · {fmtDate(p.updated_at)}</div>
                      </button>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <Badge variant="outline">{p.zone}</Badge>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      <div className="flex gap-1 flex-wrap">
                        {[...new Set((p.result?.operations || []).map((o) => o.sector))].map((s) => (
                          <Badge key={s} className={`${SECTOR_META[s].badge} border`}>{s}</Badge>
                        ))}
                        <span className="text-muted-foreground">{(p.result?.operations || []).length} op.</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right metric font-semibold">{fmtMwhc(p.result?.total_kwhc)}</td>
                    <td className="px-4 py-3 text-right metric font-bold text-genis">{fmtEur(p.result?.total_prime)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(`/projects/${p.id}`)} data-testid={`action-view-${p.id}`}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => exportCsv(p)} data-testid={`action-export-${p.id}`}>
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => duplicate(p.id)} data-testid={`action-duplicate-${p.id}`}>
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => setToDelete(p)} data-testid={`action-delete-${p.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce projet ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le projet « {toDelete?.name} » sera définitivement supprimé. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive hover:bg-destructive/90" data-testid="delete-confirm-btn">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
